import React from 'react';
import { twMerge } from 'tailwind-merge';

export type CardVariant = 'default' | 'gradient-sheen';

export interface CardProps {
  /** 'default' = flat surface card; 'gradient-sheen' = subtle depth gradient (credits/upgrade) */
  variant?: CardVariant;
  /** When true, adds interactive hover: border strengthens + glow appears. Resting cards NEVER glow. */
  interactive?: boolean;
  children?: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /** Keyboard handler — pair with role/tabIndex when the card is click-activated for a11y. */
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /** ARIA role — set to "button" when the card itself is the click target. */
  role?: string;
  /** Tab order — set to 0 when the card is keyboard-focusable. */
  tabIndex?: number;
  /** Accessible name when the card is interactive. */
  'aria-label'?: string;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  interactive = false,
  children,
  className = '',
  onClick,
  onKeyDown,
  role,
  tabIndex,
  'aria-label': ariaLabel,
}) => {
  // Base shared by both variants
  // Design system glass: card = vidro base (translúcido + blur + borda + highlight).
  const base = 'rounded-card';

  // Variant-specific fill + shadow
  // gradient-sheen: uses a token-based CSS-var gradient — NOT raw hex; referenced via var(--snapfy-*)
  const variantClasses: Record<CardVariant, string> = {
    default: 'snapfy-glass p-5',
    // linear-gradient references CSS custom properties, not raw hex — documented token exception
    'gradient-sheen': [
      // Vidro com o tint magenta→índigo do DS (antes: degradê de superfície).
      'snapfy-glass bg-snapfy-gradient-tint',
      'p-4',
    ].join(' '),
  };

  // Interactive state: glow ONLY on hover — resting cards must NEVER carry shadow-glow (T-04-07)
  const interactiveClasses = interactive
    ? 'hover:-translate-y-0.5 hover:border-snapfy-accent-border hover:shadow-selected transition-[transform,box-shadow,border-color] duration-150 ease-standard cursor-pointer'
    : '';

  return (
    <div
      // twMerge so a caller's className (e.g. p-0 for full-bleed image cards, p-4 md:p-8)
      // deterministically overrides the variant's default padding instead of colliding with it.
      className={twMerge(base, variantClasses[variant], interactiveClasses, className)}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
};

export default Card;
